import { useState, useEffect } from "react";
import LeftSidebar from "./components/LeftSidebar";
import Header from "./components/Header";
import { Menu, Trash2, Edit } from "lucide-react";
import api from "../connector/api";
import toast, { Toaster } from "react-hot-toast";

export default function SetupYearTerm() {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ----- Year states -----
  const [yearName, setYearName] = useState("");
  const [yearStart, setYearStart] = useState("");
  const [yearEnd, setYearEnd] = useState("");
  const [years, setYears] = useState([]);
  const [editingYearId, setEditingYearId] = useState(null);

  // ----- Term states -----
  const [termName, setTermName] = useState("");
  const [terms, setTerms] = useState([]);
  const [editingTermId, setEditingTermId] = useState(null);

  // ----- Default Year & Term states -----
  const [defaultYearId, setDefaultYearId] = useState("");
  const [defaultTermId, setDefaultTermId] = useState("");
  const [currentDefault, setCurrentDefault] = useState(null);

  // ----- School Year-Term list -----
  const [yearTerms, setYearTerms] = useState([]);

  // Search states
  const [yearSearch, setYearSearch] = useState("");
  const [yeartermSearch, setYearTermSearch] = useState("");

  // Pagination states
  const [yearPage, setYearPage] = useState(1);
  const [yeartermPage, setYearTermPage] = useState(1);
  const itemsPerPage = 4;
  const yeartermPerPage = 4;

  // ----- Fetch initial data -----
  useEffect(() => {
    fetchYears();
    fetchTerms();
    fetchCurrentDefault();
    fetchYearTerms();
  }, []);

  const fetchYears = async () => {
    try {
      const res = await api.get("years/");
      setYears(res.data);
    } catch {
      setYears([]);
    }
  };

  const fetchTerms = async () => {
    try {
      const res = await api.get("terms/");
      setTerms(res.data);
    } catch {
      setTerms([]);
    }
  };

  const fetchCurrentDefault = async () => {
    try {
      const res = await api.get("year-terms/current/");
      setCurrentDefault(res.data);
      if (res.data) {
        setDefaultYearId(res.data.school_year.id);
        setDefaultTermId(res.data.term.id);
      }
    } catch {
      setCurrentDefault(null);
    }
  };

  const fetchYearTerms = async () => {
    try {
      const res = await api.get("year-terms/"); // adjust endpoint if needed
      setYearTerms(res.data);
    } catch {
      setYearTerms([]);
    }
  };

  // ----- Year Handlers -----
  const handleYearSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingYearId) {
        await api.put(`years/${editingYearId}/`, {
          name: yearName,
          start_date: yearStart,
          end_date: yearEnd,
        });
        toast.success("Academic Year updated successfully");
      } else {
        await api.post("years/", {
          name: yearName,
          start_date: yearStart,
          end_date: yearEnd,
        });
        toast.success("Academic Year created successfully");
      }
      setYearName(""); setYearStart(""); setYearEnd(""); setEditingYearId(null);
      fetchYears();
      fetchYearTerms();
    } catch {
      toast.error("Failed to save Academic Year");
    }
  };

  const deleteYear = async (id) => {
    if (!window.confirm("Delete this academic year?")) return;
    try {
      await api.delete(`years/${id}/`);
      fetchYears();
      fetchYearTerms();
      toast.success("Academic Year deleted");
    } catch {
      toast.error("Failed to delete Academic Year");
    }
  };

  const editYear = (year) => {
    setYearName(year.name);
    setYearStart(year.start_date);
    setYearEnd(year.end_date);
    setEditingYearId(year.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ----- Term Handlers -----
  const handleTermSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTermId) {
        await api.put(`terms/${editingTermId}/`, { name: termName });
        toast.success("Term updated successfully");
      } else {
        await api.post("terms/", { name: termName });
        toast.success("Term created successfully");
      }
      setTermName(""); setEditingTermId(null);
      fetchTerms();
      fetchYearTerms();
    } catch {
      toast.error("Failed to save Term");
    }
  };

  const deleteTerm = async (id) => {
    if (!window.confirm("Delete this term?")) return;
    try {
      await api.delete(`terms/${id}/`);
      fetchTerms();
      fetchYearTerms();
      toast.success("Term deleted");
    } catch {
      toast.error("Failed to delete Term");
    }
  };

  const editTerm = (term) => {
    setTermName(term.name);
    setEditingTermId(term.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ----- Default School Year & Term -----
const [termStart, setTermStart] = useState("");
const [termEnd, setTermEnd] = useState("");

// Fetch currently set year-term
const fetchSetYearTerm = async () => {
  try {
    const res = await api.get("year-terms/set-year-term/");
    setCurrentDefault(res.data);
    if (res.data) {
      setDefaultYearId(res.data.school_year.id);
      setDefaultTermId(res.data.term.id);
      setTermStart(res.data.start_date);
      setTermEnd(res.data.end_date);
    }
  } catch {
    setCurrentDefault(null);
  }
};

  // Handle setting default school year-term
  const handleSetYearTerm = async (e) => {
    e.preventDefault();

    if (!defaultYearId || !defaultTermId || !termStart || !termEnd) {
      toast.error("Please select Year, Term, and provide start/end dates");
      return;
    }

    try {
      const res = await api.post("year-terms/set-year-term/", {
        school_year: defaultYearId,
        term: defaultTermId,
        start_date: termStart,
        end_date: termEnd,
      });

      if (res.data.success) {
        toast.success("School Year-Term updated successfully");
        await fetchSetYearTerm();
      } else {
        toast.error("Failed to update School Year-Term");
      }
    } catch (err) {
      const message =
        err.response?.data?.error || "Something went wrong while setting School Year-Term";
      toast.error(message);
    }
  };

  // Edit Year-Term (load into form for editing)
  const handleEditYearTerm = (yt) => {
    setDefaultYearId(yt.school_year.id);
    setDefaultTermId(yt.term.id);
    setTermStart(yt.start_date);
    setTermEnd(yt.end_date);
    toast.info("Loaded Year-Term into form for editing");
  };

  // Set Default
  const handleSetDefaultYearTerm = async (yt) => {
    try {
      await api.post("year-terms/set-year-term/", {
        school_year: yt.school_year.id,
        term: yt.term.id,
        start_date: yt.start_date,
        end_date: yt.end_date,
      });
      toast.success("Default Year-Term updated");
      fetchYearTerms(); // refresh list
    } catch (err) {
      console.error(err);
      toast.error("Failed to set default");
    }
  };

  // Delete Year-Term
  const handleDeleteYearTerm = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Year-Term?")) return;
    try {
      await api.delete(`year-terms/${id}/`);
      toast.success("Year-Term deleted");
      fetchYearTerms(); // refresh list
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete Year-Term");
    }
  };

  // Filtered + paginated years
  const filteredYears = years.filter((y) =>
    y.name.toLowerCase().includes(yearSearch.toLowerCase())
  );
  const yearTotalPages = Math.ceil(filteredYears.length / itemsPerPage);
  const paginatedYears = filteredYears.slice(
    (yearPage - 1) * itemsPerPage,
    yearPage * itemsPerPage
  );

  // Filter by year or term
  const filteredYearTerms = yearTerms.filter(
    (yt) =>
      yt.school_year?.name
        .toLowerCase()
        .includes(yeartermSearch.toLowerCase()) ||
      yt.term?.name.toLowerCase().includes(yeartermSearch.toLowerCase())
  );

  // Pagination logic
  const yeartermTotalPages = Math.ceil(filteredYearTerms.length / yeartermPerPage);
  const paginatedYearTerms = filteredYearTerms.slice(
    (yeartermPage - 1) * yeartermPerPage,
    yeartermPage * yeartermPerPage
  );


  // ----- Render -----
  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      <Toaster position="top-right" reverseOrder={false} />
      <Header />
      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && <LeftSidebar />}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="bg-white p-2 rounded shadow flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <button
                  className="p-2 rounded hover:bg-gray-100"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <Menu className="h-5 w-5 text-gray-600" />
                </button>
                <h1 className="text-xl font-bold">Setup - Year & Term</h1>
              </div>
            </div>

            {/* Academic Year Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="bg-white p-4 rounded shadow self-start">
                <h2 className="text-lg font-semibold mb-3 border-b pb-2">
                  {editingYearId ? "Edit Academic Year" : "Academic Year Form"}
                </h2>
                <form onSubmit={handleYearSubmit} className="space-y-3 text-sm">
                  <input
                    type="text"
                    value={yearName}
                    onChange={(e) => setYearName(e.target.value)}
                    placeholder="e.g. 2024-2025"
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="date"
                    value={yearStart}
                    onChange={(e) => setYearStart(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <input
                    type="date"
                    value={yearEnd}
                    onChange={(e) => setYearEnd(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-[#002855] text-white px-4 py-2 rounded hover:bg-[#f7b512]"
                  >
                    {editingYearId ? "Update Academic Year" : "Save Academic Year"}
                  </button>
                </form>
              </div>
              <div className="bg-white p-4 rounded shadow self-start">
                <div className="flex items-center justify-between mb-3 border-b pb-2">
                  <h2 className="text-lg font-semibold">Academic Years List</h2>
                  <input
                    type="text"
                    placeholder="Search year..."
                    value={yearSearch}
                    onChange={(e) => {
                      setYearSearch(e.target.value);
                      setYearPage(1); // reset to page 1 when searching
                    }}
                    className="border rounded px-2 py-1 text-sm"
                  />
                </div>

                {filteredYears.length === 0 ? (
                  <p className="text-gray-500">No years found</p>
                ) : (
                  <>
                    <table className="w-full border text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border px-2 py-1 text-left">Name</th>
                          <th className="border px-2 py-1 text-left">Start</th>
                          <th className="border px-2 py-1 text-left">End</th>
                          <th className="border px-2 py-1 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedYears.map((y) => (
                          <tr key={y.id}>
                            <td className="border px-2 py-1">{y.name}</td>
                            <td className="border px-2 py-1">{y.start_date}</td>
                            <td className="border px-2 py-1">{y.end_date}</td>
                            <td className="border px-2 py-1 text-left">
                              <button
                                className="text-blue-600 hover:underline mr-2"
                                onClick={() => editYear(y)}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="text-red-600 hover:underline"
                                onClick={() => deleteYear(y.id)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-3 text-sm">
                      <span>
                        Page {yearPage} of {yearTotalPages}
                      </span>
                      <div className="space-x-2">
                        <button
                          disabled={yearPage === 1}
                          onClick={() => setYearPage((p) => p - 1)}
                          className="px-2 py-1 border rounded disabled:opacity-50"
                        >
                          Prev
                        </button>
                        <button
                          disabled={yearPage === yearTotalPages}
                          onClick={() => setYearPage((p) => p + 1)}
                          className="px-2 py-1 border rounded disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Term Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              <div className="bg-white p-4 rounded shadow self-start">
                <h2 className="text-lg font-semibold mb-3 border-b pb-2">
                  {editingTermId ? "Edit Term" : "Setup Term"}
                </h2>
                <form onSubmit={handleTermSubmit} className="space-y-3 text-sm">
                  <input
                    type="text"
                    value={termName}
                    onChange={(e) => setTermName(e.target.value)}
                    placeholder="e.g. Term 1, Fall"
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <button
                    type="submit"
                    className="bg-[#002855] text-white px-4 py-2 rounded hover:bg-[#f7b512]"
                  >
                    {editingTermId ? "Update Term" : "Save Term"}
                  </button>
                </form>
              </div>

              <div className="bg-white p-4 rounded shadow self-start">
                <h2 className="text-lg font-semibold mb-3 border-b pb-2">Terms List</h2>
                {terms.length === 0 ? (
                  <p className="text-gray-500">No terms found</p>
                ) : (
                  <table className="w-full border text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border px-2 py-1 text-left">Name</th>
                        <th className="border px-2 py-1 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {terms.map((t) => (
                        <tr key={t.id}>
                          <td className="border px-2 py-1">{t.name}</td>
                          <td className="border px-2 py-1 text-center">
                            <button
                              className="text-blue-600 hover:underline mr-2"
                              onClick={() => editTerm(t)}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="text-red-600 hover:underline"
                              onClick={() => deleteTerm(t.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Default Year & Term */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-4 rounded shadow self-start">
                <h2 className="text-lg font-semibold mb-3 border-b pb-2">Setup Academic Term</h2>
                <form onSubmit={handleSetYearTerm} className="space-y-4 text-sm">
                  {/* Row 1: Year + Term */}
                  <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Academic Year</label>
                      <select
                        value={defaultYearId}
                        onChange={(e) => setDefaultYearId(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                        required
                      >
                        <option value="">-- Select Year --</option>
                        {years.map((y) => (
                          <option key={y.id} value={y.id}>{y.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Term</label>
                      <select
                        value={defaultTermId}
                        onChange={(e) => setDefaultTermId(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                        required
                      >
                        <option value="">-- Select Term --</option>
                        {terms.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Term Start + End Date */}
                  <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Term Start Date</label>
                      <input
                        type="date"
                        value={termStart}
                        onChange={(e) => setTermStart(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                        required
                      />
                    </div>

                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">Term End Date</label>
                      <input
                        type="date"
                        value={termEnd}
                        onChange={(e) => setTermEnd(e.target.value)}
                        className="w-full border rounded px-3 py-2"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      className="bg-[#002855] text-white px-4 py-2 rounded hover:bg-[#f7b512]"
                    >
                      Create Academic Term
                    </button>
                  </div>
                </form>

                {/* Display currently set year-term */}
                {currentDefault && (
                  <div className="mt-4 p-3 bg-yellow-100 rounded border-l-4 border-yellow-500">
                    <p className="text-sm font-semibold text-gray-800">Currently Set School Year-Term:</p>
                    <p className="text-sm text-gray-700">
                      {currentDefault.school_year.name} ({currentDefault.start_date} - {currentDefault.end_date}) - {currentDefault.term.name}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="gap-6 mb-8">
              <div className="bg-white p-4 rounded shadow self-start">
                {/* Header with search */}
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold">Academic Terms List</h2>
                  <input
                    type="text"
                    placeholder="Search term..."
                    value={yeartermSearch}
                    onChange={(e) => {
                      setYearTermSearch(e.target.value);
                      setYearTermPage(1); // reset to first page on search
                    }}
                    className="border rounded px-2 py-1 text-sm"
                  />
                </div>

                {/* Table or message */}
                {filteredYearTerms.length === 0 ? (
                  <p className="text-gray-500">No year-term data found</p>
                ) : (
                  <>
                    <table className="w-full border text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border px-2 py-1 text-left">Academic Year</th>
                          <th className="border px-2 py-1 text-left">Term</th>
                          <th className="border px-2 py-1 text-left">Start Date</th>
                          <th className="border px-2 py-1 text-left">End Date</th>
                          <th className="border px-2 py-1 text-left">Default</th>
                          <th className="border px-2 py-1 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedYearTerms.map((yt) => (
                          <tr key={yt.id}>
                            <td className="border px-2 py-1">{yt.school_year?.name}</td>
                            <td className="border px-2 py-1">{yt.term?.name}</td>
                            <td className="border px-2 py-1">{yt.start_date}</td>
                            <td className="border px-2 py-1">{yt.end_date}</td>
                            <td className="border px-2 py-1">
                              {yt.is_current ? (
                                <span className="text-green-600 font-semibold">Default</span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="border px-2 py-1 space-x-2">
                              {/* Edit */}
                              <button
                                onClick={() => handleEditYearTerm(yt)}
                                className="px-1 py-1 text-blue-500 rounded text-xs"
                              >
                                <Edit size={16} />
                              </button>

                              {/* Set Default */}
                              {!yt.is_current && (
                                <button
                                  onClick={() => handleSetDefaultYearTerm(yt)}
                                  className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                                >
                                  Set Default
                                </button>
                              )}

                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteYearTerm(yt.id)}
                                className="px-1 py-1 text-red-500 rounded text-xs"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="flex justify-between items-center mt-3 text-sm">
                      <span>
                        Page {yeartermPage} of {yeartermTotalPages}
                      </span>
                      <div className="space-x-2">
                        <button
                          disabled={yeartermPage === 1}
                          onClick={() => setYearTermPage((p) => p - 1)}
                          className="px-2 py-1 border rounded disabled:opacity-50"
                        >
                          Prev
                        </button>
                        <button
                          disabled={yeartermPage === yeartermTotalPages}
                          onClick={() => setYearTermPage((p) => p + 1)}
                          className="px-2 py-1 border rounded disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
